import { useEffect, useState } from "react";
import { ZeltDetail } from "@/components/ZeltDetail";
import { ZeltGrid } from "@/components/ZeltGrid";
import { Container } from "@/components/ui/container";
import { useSeo, generateProductSchema, generateBreadcrumbSchema } from "@/hooks/useSeo";
import type { Zelt } from "@/data/zelte";
import { kategorien } from "@/data/zelte";

interface ZeltSeiteProps {
  slug: string;
  onNavigate: (path: string) => void;
  onToggleVergleich: (zelt: Zelt) => void;
  isInVergleich: (id: string) => boolean;
  vergleichIds: string[];
}

export function ZeltSeite({ slug, onNavigate, onToggleVergleich, isInVergleich, vergleichIds }: ZeltSeiteProps) {
  const [zelt, setZelt] = useState<Zelt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [relatedZelte, setRelatedZelte] = useState<Zelt[]>([]);

  useEffect(() => {
    // Scroll to top when navigating to a new tent
    window.scrollTo(0, 0);

    setLoading(true);
    setError(false);

    fetch(`/api/zelte/${slug}`)
      .then(res => {
        if (!res.ok) throw new Error("Not found");
        return res.json();
      })
      .then(data => {
        setZelt(data);
        setLoading(false);
        // Fetch related tents
        fetch(`/api/zelte/${slug}/related`)
          .then(res => res.json())
          .then(related => setRelatedZelte(related))
          .catch(() => {});
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [slug]);

  // SEO
  useSeo(
    zelt
      ? {
          title: `${zelt.name} - ${zelt.marke} | zelt.kaufen`,
          description: zelt.kurzbeschreibung,
          keywords: `${zelt.name}, ${zelt.marke}, ${kategorien.find(k => k.id === zelt.kategorie)?.name}, Zelt kaufen, ${zelt.personen} Personen Zelt`,
          image: zelt.bilder[0],
          type: "product",
          price: zelt.preis,
          availability: zelt.verfuegbar ? "instock" : "outofstock",
          schema: {
            "@context": "https://schema.org",
            "@graph": [
              generateProductSchema({
                name: zelt.name,
                description: zelt.beschreibung,
                image: zelt.bilder[0],
                price: zelt.preis,
                brand: zelt.marke,
                rating: zelt.bewertung,
                reviewCount: zelt.bewertungAnzahl,
                availability: zelt.verfuegbar ? "instock" : "outofstock",
              }),
              generateBreadcrumbSchema([
                { name: "Startseite", url: window.location.origin },
                { name: "Zelte", url: `${window.location.origin}/zelte` },
                { name: kategorien.find(k => k.id === zelt.kategorie)?.name || "", url: `${window.location.origin}/zelte?kategorie=${zelt.kategorie}` },
                { name: zelt.name, url: window.location.href },
              ]),
            ],
          },
        }
      : {
          title: "Laden... | zelt.kaufen",
          description: "Zelt wird geladen",
        }
  );

  if (loading) {
    return (
      <Container className="py-8">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-48 mb-4" />
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="aspect-square bg-muted rounded-xl" />
            <div className="space-y-4">
              <div className="h-4 bg-muted rounded w-24" />
              <div className="h-10 bg-muted rounded w-64" />
              <div className="h-4 bg-muted rounded w-32" />
              <div className="h-20 bg-muted rounded" />
              <div className="h-10 bg-muted rounded w-40" />
            </div>
          </div>
        </div>
      </Container>
    );
  }

  if (error || !zelt) {
    return (
      <Container className="py-16 text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
          <svg viewBox="0 0 100 100" className="w-10 h-10 text-muted-foreground">
            <polygon points="50,15 85,85 15,85" fill="currentColor" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold mb-2">Zelt nicht gefunden</h1>
        <p className="text-muted-foreground mb-6">
          Das gesuchte Zelt konnte leider nicht gefunden werden.
        </p>
        <button
          onClick={() => onNavigate("/zelte")}
          className="text-primary hover:underline"
        >
          Zurück zur Übersicht
        </button>
      </Container>
    );
  }

  return (
    <>
      <ZeltDetail
        zelt={zelt}
        onNavigate={onNavigate}
        onToggleVergleich={onToggleVergleich}
        isInVergleich={isInVergleich(zelt.id)}
      />
      {relatedZelte.length > 0 && (
        <Container className="pb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Ähnliche Zelte</h2>
          <ZeltGrid
            zelte={relatedZelte}
            onNavigate={onNavigate}
            onToggleVergleich={onToggleVergleich}
            vergleichIds={vergleichIds}
            loading={false}
          />
        </Container>
      )}
    </>
  );
}
